import { VipPanel } from './VipPanel'
import { UiIcon } from './UiIcon'
import { useEffect, useRef, type KeyboardEvent } from 'react'
import { AccountPanel } from './AccountPanel'
import { HistoryPanel, MedalsPanel, SettingsPanel } from './SupportPanels'
import type { PanelDialogProps } from './view-types'

export function PanelDialog({ panel, state, locationLabel, preferences, authSession, authLoading, authError, cloudStatus, cloudDocument, saveSelection, cloudError, cloudActionError, cloudActionBusy, onClose, onPreferences, onReset, onLogin, onLogout, onSync, onChooseGuest, onChooseCloud, onRefreshAuth, onResetCloud }: PanelDialogProps) {
  const titles: Record<Exclude<PanelDialogProps['panel'], null>, string> = { history: '人生履历', medals: '图鉴与成就', settings: '阅读设置', vip: '黑金VIP · 韭菜通行证', account: '个人详情' }
  const mustChooseSave = panel === 'account' && Boolean(saveSelection)
  const dialogRef = useRef<HTMLElement | null>(null)
  const openerRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const background = Array.from(document.querySelectorAll<HTMLElement>('.site-header, .page-wrap, .site-footer'))
    background.forEach(element => {
      element.setAttribute('aria-hidden', 'true')
      ;(element as HTMLElement & { inert?: boolean }).inert = true
    })
    document.body.style.overflow = 'hidden'
    const focusInitial = () => {
      const scope = dialogRef.current?.querySelector<HTMLElement>('#pay-overlay, #vip-ceremony, #svip-ceremony') || dialogRef.current
      const focusable = scope?.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ;(focusable || dialogRef.current)?.focus()
    }
    const timer = window.setTimeout(focusInitial, 0)
    return () => {
      window.clearTimeout(timer)
      background.forEach(element => {
        element.removeAttribute('aria-hidden')
        ;(element as HTMLElement & { inert?: boolean }).inert = false
      })
      document.body.style.overflow = ''
      window.setTimeout(() => {
        const opener = openerRef.current
        const usable = opener && opener.isConnected && !opener.hasAttribute('disabled') && !opener.hasAttribute('aria-hidden')
        const fallback = document.querySelector<HTMLElement>('.site-header button:not([disabled]), .page-wrap button:not([disabled]), .site-footer button:not([disabled])')
        ;(usable ? opener : fallback)?.focus()
      }, 0)
    }
  }, [])

  useEffect(() => {
    const body = dialogRef.current?.querySelector<HTMLElement>('.modal-body')
    if (body) body.scrollTop = 0
    const focusable = dialogRef.current?.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    focusable?.focus()
  }, [panel])

  const onDialogKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      if (!mustChooseSave) onClose()
      return
    }
    if (event.key !== 'Tab') return
    const scope = dialogRef.current?.querySelector<HTMLElement>('#pay-overlay, #vip-ceremony, #svip-ceremony') || dialogRef.current
    const focusable = Array.from(scope?.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') || []).filter(element => !element.hasAttribute('disabled') && !element.closest('[inert]') && element.getClientRects().length > 0)
    if (!focusable.length) {
      event.preventDefault()
      dialogRef.current?.focus()
      return
    }
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return <div className="modal-backdrop" onMouseDown={event => { if (!mustChooseSave && event.target === event.currentTarget) onClose() }}><section ref={dialogRef} className="modal-sheet" data-panel={panel} role="dialog" tabIndex={-1} aria-modal="true" aria-labelledby="panel-title" onKeyDown={onDialogKeyDown}><div className="modal-handle" /><div className="modal-heading"><h2 id="panel-title"><span className="dialog-title-icon"><UiIcon name={panel} /></span>{titles[panel]}</h2>{!mustChooseSave && <button className="close-button" onClick={onClose} aria-label="关闭"><UiIcon name="close" /></button>}</div><div className="modal-body" tabIndex={0} aria-label="弹窗内容">{panel === 'history' && <HistoryPanel state={state} />}{panel === 'medals' && <MedalsPanel state={state} />}{panel === 'settings' && <SettingsPanel preferences={preferences} onPreferences={onPreferences} />}{panel === 'vip' && <VipPanel onClose={onClose} />}{panel === 'account' && <AccountPanel state={state} locationLabel={locationLabel} session={authSession} loading={authLoading} error={authError} cloudStatus={cloudStatus} cloudDocument={cloudDocument} saveSelection={saveSelection} cloudError={cloudError} cloudActionError={cloudActionError} cloudActionBusy={cloudActionBusy} onLogin={onLogin} onLogout={onLogout} onReset={onReset} onSync={onSync} onChooseGuest={onChooseGuest} onChooseCloud={onChooseCloud} onRefreshAuth={onRefreshAuth} onResetCloud={onResetCloud} />}</div></section></div>
}
